// web/src/screens/Preview.tsx — the prospect's whole dashboard (portal spec 24 / DECISIONS X8). Replaces spec 13's
// Prospect placeholder. One screen, by state: the form → "We're building your preview" → their demo + "Ready to go?"
// ($5,000 Payment Link) → (paid) the client dashboard. Also: declined, put away, and the form switched off.
// Phone-first: one column, 44px+ targets, the preview opens in a new tab (demo hosts may refuse iframes).
import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { logout } from '../lib/auth';
import { BRAND } from '../lib/brand';
import { apiErrorStatus, apiErrorText } from '../lib/errors';
import { FormField } from '../components/FormField';
import { Icon } from '../components/Icon';
import { Skeleton } from '../components/Skeleton';
import { Turnstile, turnstileAvailable } from '../components/Turnstile';
import { toast } from '../components/Toast';

const EMPTY = { businessName: '', currentSiteUrl: '', socials: '', biggestIssue: '', phone: '' };
const socialsOf = (s: string) => s.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean).slice(0, 5);

function sendError(e: unknown): string {
  const st = apiErrorStatus(e);
  if (st === 429) return 'You’ve sent this a few times already. Please try again later.';
  if (st === 404) return 'The form isn’t open right now. Please check back soon.';
  return apiErrorText(e, 'Something went wrong. Please try again.');
}

export function Preview({ email }: { email: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['preview'], queryFn: () => api.myPreview() });
  const [f, setF] = useState(EMPTY);
  const [token, setToken] = useState('');
  const [reset, setReset] = useState(0);
  const onToken = useCallback((t: string) => setToken(t), []);
  const send = useMutation({
    mutationFn: () => api.sendPreview({
      businessName: f.businessName.trim(), currentSiteUrl: f.currentSiteUrl.trim() || undefined, phone: f.phone.trim() || undefined,
      socials: socialsOf(f.socials), biggestIssue: f.biggestIssue.trim(), turnstileToken: token,
    }),
    onSuccess: () => {
      toast({ kind: 'success', message: 'Got it. We’re on it.' });
      void qc.invalidateQueries({ queryKey: ['preview'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e: unknown) => { toast({ kind: 'error', message: sendError(e) }); setReset((n) => n + 1); },
  });

  const wrap = (body: ReactNode) => (
    <div className="mx-auto w-full max-w-lg px-4 py-10">
      {body}
      <div className="mt-10 border-t border-hair pt-4">
        <p className="break-words text-xs text-muted">Signed in as {email}</p>
        <button type="button" onClick={() => logout()} className="mt-1 inline-flex min-h-11 items-center gap-2 text-sm text-muted hover:text-ink">
          <Icon name="logout" size={16} />Sign out
        </button>
      </div>
    </div>
  );

  if (q.isLoading) return wrap(<Skeleton variant="card" count={2} />);
  if (q.isError || !q.data) return wrap(<>
    <h1 className="font-head text-xl text-ink">We couldn’t load this page</h1>
    <button type="button" onClick={() => void q.refetch()} className="mt-4 min-h-11 rounded-md border border-hair px-4 text-sm text-ink">Try again</button>
  </>);
  const p = q.data;

  if (p.demo) return wrap(<>
    <h1 className="font-head text-2xl text-ink">Your new website is ready</h1>
    <p className="mt-2 text-sm text-muted">Here’s the preview we built for {p.lead?.businessName ?? p.demo.name}. Have a look on your phone.</p>
    <a href={p.demo.previewUrl} target="_blank" rel="noopener noreferrer"
      className="mt-6 flex min-h-12 items-center justify-center gap-2 rounded-md border border-hair bg-surface px-4 text-ink">
      <Icon name="external" size={16} />See your new website
    </a>
    {p.buyUrl && <>
      <a href={p.buyUrl} data-widget="preview-buy"
        className="mt-4 flex min-h-12 items-center justify-center rounded-md bg-run px-4 py-3 text-center font-medium leading-snug text-runink">
        Ready to go? $5,000, first 30 days included
      </a>
      <p className="mt-3 text-xs text-muted">Then $100 a month, or $1,000 a year. We need three things from you. Once we have them, you’re live.</p>
    </>}
  </>);

  if (p.demoEnded) return wrap(<>
    <h1 className="font-head text-xl text-ink">Your preview has been put away</h1>
    <p className="mt-2 text-sm text-muted">Reply to our email and we’ll bring it back.</p>
  </>);

  if (p.lead?.status === 'sold') return wrap(<>
    <h1 className="font-head text-xl text-ink">Thank you!</h1>
    <p className="mt-2 text-sm text-muted">Your dashboard is getting ready.</p>
    <button type="button" onClick={() => void qc.invalidateQueries()} className="mt-4 min-h-11 rounded-md bg-run px-4 text-sm font-medium text-runink">Open my dashboard</button>
  </>);

  if (p.lead?.status === 'declined') return wrap(<>
    <h1 className="font-head text-xl text-ink">Thanks for asking</h1>
    <p className="mt-2 text-sm text-muted">We couldn’t build a preview for this one. If something has changed, send the form again.</p>
    {p.formEnabled && (
      <button type="button" onClick={() => qc.setQueryData(['preview'], { ...p, lead: null })}
        className="mt-4 min-h-11 rounded-md border border-hair px-4 text-sm text-ink">Send it again</button>
    )}
  </>);

  if (p.lead) return wrap(<>
    <h1 className="font-head text-xl text-ink">We’re building your preview</h1>
    <p className="mt-2 text-sm text-muted">We’ll email you when it’s ready to look at. It will show up right here.</p>
  </>);

  if (!p.formEnabled) return wrap(<>
    <h1 className="font-head text-xl text-ink">This dashboard is for {BRAND.name} clients</h1>
    <p className="mt-2 text-sm text-muted">If you were sent an invite link, open it again on this device.</p>
  </>);

  const ok = f.businessName.trim().length >= 2 && f.biggestIssue.trim().length >= 10 && !!token;
  const submit = (e: FormEvent) => { e.preventDefault(); if (ok && !send.isPending) send.mutate(); };
  return wrap(<form onSubmit={submit} noValidate>
    <h1 className="font-head text-2xl text-ink">Get a free preview of your new website</h1>
    <p className="mt-2 text-sm text-muted">Tell us where you are now. We’ll build a preview and show it to you here. Free, no card.</p>
    <div className="mt-6 grid gap-4">
      <FormField label="Business name" name="businessName" required value={f.businessName}
        onChange={(v) => setF({ ...f, businessName: v })}
        validate={(v) => (v.trim().length >= 2 ? null : 'What’s the business called?')} />
      <FormField label="Your current website (if you have one)" name="currentSiteUrl" type="url" inputMode="url" value={f.currentSiteUrl}
        onChange={(v) => setF({ ...f, currentSiteUrl: v })} />
      <FormField label="Your socials (Facebook, Instagram, Google… paste links)" name="socials" value={f.socials}
        helper="Up to 5 links, separated by spaces or commas." onChange={(v) => setF({ ...f, socials: v })} />
      <FormField label="What’s the biggest problem with your current website?" name="biggestIssue" textarea rows={4} required
        value={f.biggestIssue} onChange={(v) => setF({ ...f, biggestIssue: v })}
        validate={(v) => (v.trim().length >= 10 ? null : 'A sentence or two is plenty.')} />
      <FormField label="Phone (optional)" name="phone" type="tel" inputMode="tel" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} />
      <Turnstile onToken={onToken} resetSignal={reset} />
      <button type="submit" disabled={!ok || send.isPending || !turnstileAvailable} data-widget="preview-form"
        className="min-h-12 rounded-md bg-run px-4 font-medium text-runink disabled:opacity-50">
        {send.isPending ? 'Sending…' : 'Get my free preview'}
      </button>
    </div>
  </form>);
}
